'use client'

import { useState, useEffect, useCallback } from 'react'
import { X, RefreshCw, Loader2 } from 'lucide-react'
import { REFERENCE_PRICE_LABEL } from '@/lib/pricing-display'
import { Z } from '@/lib/z-index'

interface UserRow {
  id: string
  email: string
  full_name: string | null
  created_at: string
  is_pro: boolean
  plan: string
  credits: number
  role: string | null
  transcription_count: number
  correction_count: number
  note_count: number
  codification_count: number
  rapport_count: number
}

interface AdminStats {
  total: number
  proUsers: number
  freeUsers: number
  activeUsers: number
  totals: {
    transcriptions: number
    corrections: number
    notes: number
    codifications: number
    rapports: number
  }
  allUsers: UserRow[]
  revenue: {
    proSubscriptions: number
    simulatedRevenue: number
    /** Devise du revenu simulé — celle du prix de référence (D-020). */
    currency: string
  }
}

interface AdminPanelProps {
  open: boolean
  onClose: () => void
}

function StatCard({ icon, label, value, sub }: { icon: string; label: string; value: number | string; sub?: string }) {
  return (
    <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '14px 16px', border: '1px solid #F3F4F6' }}>
      <div style={{ fontSize: 20, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', letterSpacing: '0.08em', fontFamily: 'monospace', margin: '20px 0 10px' }}>
      {children}
    </div>
  )
}

function isAdminRole(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'administrateur'
}

function PlanBadge({ plan, role }: { plan: string; role: string | null }) {
  const isAdmin = isAdminRole(role)
  const isPro = plan === 'pro' || plan === 'admin'
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
      background: isAdmin ? '#FEF3C7' : isPro ? '#D1FAE5' : '#F3F4F6',
      color: isAdmin ? '#92400E' : isPro ? '#065F46' : '#6B7280',
    }}>
      {isAdmin ? 'Admin' : isPro ? 'Pro' : 'Free'}
    </span>
  )
}

export default function AdminPanel({ open, onClose }: AdminPanelProps) {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [changingPlan, setChangingPlan] = useState<string | null>(null)
  const [planMsg, setPlanMsg] = useState<{ id: string; msg: string; ok: boolean } | null>(null)

  const fetchStats = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/admin/stats')
      if (!res.ok) throw new Error('Accès refusé ou erreur serveur')
      const data = await res.json() as { stats: AdminStats }
      setStats(data.stats)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) fetchStats()
  }, [open, fetchStats])

  const handleChangePlan = async (user: UserRow) => {
    const newPlan = user.is_pro ? 'free' : 'pro'
    setChangingPlan(user.id)
    setPlanMsg(null)
    try {
      const res = await fetch('/api/admin/set-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId: user.id, plan: newPlan }),
      })
      if (!res.ok) throw new Error('Erreur serveur')
      setPlanMsg({ id: user.id, msg: `→ ${newPlan}`, ok: true })
      await fetchStats()
    } catch (e) {
      setPlanMsg({ id: user.id, msg: e instanceof Error ? e.message : 'Erreur', ok: false })
    } finally {
      setChangingPlan(null)
    }
  }

  return (
    <>
      {open && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: Z.adminBackdrop, background: 'rgba(0,0,0,0.30)' }}
          onClick={onClose}
        />
      )}

      <div
        style={{
          position: 'fixed', top: 0, right: open ? 0 : -520, bottom: 0,
          width: 500, background: '#fff', zIndex: Z.admin,
          boxShadow: '-4px 0 32px rgba(0,0,0,0.14)',
          transition: 'right 0.28s cubic-bezier(.4,0,.2,1)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, background: '#1B4332' }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: '#fff', margin: 0 }}>🛠️ Administration Qualyse</h3>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>Tableau de bord super-admin</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={fetchStats}
              disabled={loading}
              style={{ background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center' }}
              title="Rafraîchir"
            >
              {loading ? <Loader2 size={16} color="#fff" className="animate-spin" /> : <RefreshCw size={16} color="#fff" />}
            </button>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center' }}>
              <X size={16} color="#fff" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, padding: '16px 24px', overflowY: 'auto' }}>
          {loading && !stats && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10, color: '#6B7280' }}>
              <Loader2 size={20} className="animate-spin" />
              <span>Chargement des données...</span>
            </div>
          )}

          {error && (
            <div style={{ padding: '12px 16px', background: '#FEF2F2', borderRadius: 8, border: '1px solid #FECACA', color: '#DC2626', fontSize: 13, marginTop: 12 }}>
              ⚠️ {error}
            </div>
          )}

          {stats && (
            <>
              {/* Global stats */}
              <SectionTitle>── STATISTIQUES GLOBALES</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <StatCard icon="👥" label="Utilisateurs inscrits" value={stats.total} />
                <StatCard icon="📊" label="Utilisateurs actifs" value={stats.activeUsers} sub="au moins 1 utilisation" />
                <StatCard icon="⭐" label="Utilisateurs Pro" value={stats.proUsers} />
                <StatCard icon="🆓" label="Plan gratuit" value={stats.freeUsers} />
              </div>

              {/* Module usage totals */}
              <SectionTitle>── UTILISATIONS TOTALES (CUMULATIF)</SectionTitle>
              <div style={{ background: '#F9FAFB', borderRadius: 10, border: '1px solid #F3F4F6', overflow: 'hidden' }}>
                {[
                  { emoji: '🎙️', label: 'Transcriptions', value: stats.totals.transcriptions },
                  { emoji: '✏️', label: 'Corrections', value: stats.totals.corrections },
                  { emoji: '📋', label: 'Notes techniques', value: stats.totals.notes },
                  { emoji: '🏷️', label: 'Codifications', value: stats.totals.codifications },
                  { emoji: '📊', label: 'Rapports', value: stats.totals.rapports },
                ].map(({ emoji, label, value }, i, arr) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: i < arr.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                    <span style={{ fontSize: 13, color: '#374151' }}>{emoji} {label}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{value}</span>
                  </div>
                ))}
              </div>

              {/* All users with plan management */}
              <SectionTitle>── TOUS LES UTILISATEURS ({stats.total})</SectionTitle>
              {stats.allUsers.length === 0 ? (
                <p style={{ fontSize: 13, color: '#6B7280', fontStyle: 'italic' }}>Aucun utilisateur</p>
              ) : (
                <div style={{ background: '#F9FAFB', borderRadius: 10, border: '1px solid #F3F4F6', overflow: 'hidden' }}>
                  {stats.allUsers.map((u, i) => {
                    const totalUsage = u.transcription_count + u.correction_count + u.note_count + u.codification_count + u.rapport_count
                    return (
                      <div key={u.id} style={{ padding: '10px 14px', borderBottom: i < stats.allUsers.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 }}>
                                {u.full_name || u.email}
                              </span>
                              <PlanBadge plan={u.plan} role={u.role} />
                            </div>
                            {u.full_name && (
                              <div style={{ fontSize: 10, color: '#6B7280', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                            )}
                            <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>
                              {u.credits} crédits · {totalUsage} util. · {new Date(u.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                            </div>
                            {planMsg?.id === u.id && (
                              <div style={{ fontSize: 10, marginTop: 2, color: planMsg.ok ? '#065F46' : '#DC2626', fontWeight: 600 }}>{planMsg.msg}</div>
                            )}
                          </div>
                          {!isAdminRole(u.role) && (
                            <button
                              onClick={() => handleChangePlan(u)}
                              disabled={changingPlan === u.id}
                              style={{
                                fontSize: 10, fontWeight: 700, padding: '4px 9px', borderRadius: 4, flexShrink: 0,
                                background: u.is_pro ? '#FEF2F2' : '#D1FAE5',
                                color: u.is_pro ? '#DC2626' : '#065F46',
                                border: '1px solid', borderColor: u.is_pro ? '#FECACA' : '#A7F3D0',
                                cursor: changingPlan === u.id ? 'wait' : 'pointer',
                                opacity: changingPlan === u.id ? 0.6 : 1,
                              }}
                            >
                              {changingPlan === u.id ? '...' : u.is_pro ? '→ Free' : '→ Pro'}
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Revenue */}
              <SectionTitle>── REVENUS (MODE TEST)</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
                <StatCard icon="💰" label="Abonnements Pro" value={stats.revenue.proSubscriptions} />
                {/* Devise et multiplicateur dérivés du prix de référence (D-020).
                    « 20$ » affichait un revenu en dollars alors que le prix de
                    référence est en euros — la faute de D-019, ici sur un
                    indicateur de pilotage. */}
                <StatCard icon="💵" label="Revenu simulé" value={`${stats.revenue.simulatedRevenue} ${stats.revenue.currency}`} sub={`${stats.revenue.proSubscriptions} × ${REFERENCE_PRICE_LABEL}`} />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
